<app-report-shell
  [user]="user"
  [currentTime]="''"
  [sidebarCollapsed]="sidebarCollapsed"
  [mobileSidebarOpen]="mobileSidebarOpen"
  (menuToggle)="handleMenuToggle()"
  (closeMobileSidebar)="closeMobileSidebar()"
>
  <main class="peak-report-page log-sheet-page">
    <section class="log-sheet-page__panel app-card">
      <div class="log-sheet-page__panel-header">
        <div class="log-sheet-page__heading-copy">
          <h2 class="app-title-serif">Peak Report</h2>
        </div>

        <button
          type="button"
          class="app-button app-button--primary log-sheet-filters__export"
          [disabled]="isLoading || isExporting"
          (click)="handleExportToExcel()"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M12 3v10"></path>
            <path d="m8 9 4 4 4-4"></path>
            <path d="M5 17v2h14v-2"></path>
          </svg>
          <span>{{ isExporting ? 'Exporting...' : 'Export to Excel' }}</span>
        </button>
      </div>

      <div class="log-sheet-page__panel-body">
        <div class="log-sheet-page__filters-row">
          <form class="log-sheet-filters" [formGroup]="filtersForm">
            <label class="app-form-field log-sheet-filters__field" [attr.for]="isBsDateMode ? 'log-sheet-bs-date-button' : 'log-sheet-ad-date-input'">
              <span class="app-form-label">Select Date</span>
              <div class="log-sheet-filters__input-shell log-sheet-filters__input-shell--bs" #bsDatePickerShell>
                @if (isBsDateMode) {
                  <button
                    type="button"
                    id="log-sheet-bs-date-button"
                    class="app-form-input log-sheet-filters__date-input log-sheet-filters__date-input--display"
                    aria-haspopup="dialog"
                    [attr.aria-expanded]="bsCalendarOpen"
                    (click)="openDatePicker()"
                  >
                    <span>{{ getFilterDateLabel() }}</span>
                    <span class="log-sheet-filters__date-icon" aria-hidden="true">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round">
                        <rect x="3" y="5" width="18" height="16" rx="2"></rect>
                        <path d="M8 3v4"></path>
                        <path d="M16 3v4"></path>
                        <path d="M3 11h18"></path>
                      </svg>
                    </span>
                  </button>
                  @if (bsCalendarOpen) {
                    <dialog class="log-sheet-bs-calendar" open aria-label="Nepali calendar date picker">
                      <div class="log-sheet-bs-calendar__header">
                        <button type="button" class="log-sheet-bs-calendar__nav" aria-label="Previous month" (click)="shiftBsCalendarMonth(-1)">‹</button>
                        <div class="log-sheet-bs-calendar__title">
                          {{ bsCalendarMonth?.bs_month_name || 'Nepali Calendar' }} {{ bsCalendarMonth?.bs_year || '' }}
                        </div>
                        <button type="button" class="log-sheet-bs-calendar__nav" aria-label="Next month" (click)="shiftBsCalendarMonth(1)">›</button>
                      </div>
                      <div class="log-sheet-bs-calendar__weekdays">
                        @for (weekday of bsCalendarWeekdays; track weekday) {
                          <span>{{ weekday }}</span>
                        }
                      </div>
                      @if (bsCalendarLoading) {
                        <div class="log-sheet-bs-calendar__loading">Loading calendar...</div>
                      } @else {
                        <div class="log-sheet-bs-calendar__grid">
                          @for (cell of (bsCalendarMonth?.cells || []); track cell.bs_date) {
                            <button
                              type="button"
                              class="log-sheet-bs-calendar__day"
                              [class.log-sheet-bs-calendar__day--muted]="!cell.in_current_month"
                              [class.log-sheet-bs-calendar__day--selected]="cell.is_selected"
                              [class.log-sheet-bs-calendar__day--today]="cell.is_today"
                              (click)="selectBsCalendarCell(cell)"
                            >
                              {{ cell.bs_date_label }}
                            </button>
                          }
                        </div>
                      }
                    </dialog>
                  }
                  <input #hiddenDateInput id="log-sheet-bs-date-input" class="log-sheet-filters__hidden-date-input" type="date" formControlName="date" />
                } @else {
                  <input id="log-sheet-ad-date-input" class="app-form-input log-sheet-filters__date-input" type="date" formControlName="date" />
                }
              </div>
            </label>

            <div >
              <button
                type="button"
                class="app-button app-button--secondary equal app-filter-actions__button"
                [disabled]="isLoading || isExporting"
                (click)="clearFilters()"
              >
                Clear
              </button>
            </div>
          </form>
        </div>

        <div #tableWrap class="log-sheet-page__table-wrap app-table-scroll">
          <table class="log-sheet-table">
            <thead>
              <tr>
                <th scope="col" class="log-sheet-table__sticky-column log-sheet-table__header--title-case" rowspan="2">Power House / Time</th>
                @for (group of blockGroups; track trackByBlockGroup($index, group)) {
                  <th
                    scope="colgroup"
                    [attr.colspan]="group.span"
                    [class]="'log-sheet-table__band log-sheet-table__band--' + group.period"
                  >
                    {{ group.label }}
                  </th>
                }
              </tr>
              <tr>
                @for (block of blockColumns; track trackByBlock($index, block)) {
                  <th
                    scope="col"
                    [class]="'log-sheet-table__hour-cell log-sheet-table__hour-cell--' + block.period"
                    [class.peak-report-table__block-cell--peak]="block.is_peak === true"
                  >
                    {{ block.label }}
                  </th>
                }
              </tr>
            </thead>
            <tbody>
              @if (isLoading) {
                <tr>
                  <td [attr.colspan]="getEmptyColspan()" class="log-sheet-page__empty-state">Loading log sheet data...</td>
                </tr>
              } @else if (rows.length) {
                @for (row of rows; track trackByRow($index, row)) {
                  @if (isHierarchyTotalRow(row)) {
                    <tr [class]="'log-sheet-table__hierarchy-row log-sheet-table__hierarchy-row--' + row.row_kind">
                      <td class="log-sheet-table__sticky-column">
                        <div [class]="'log-sheet-table__station log-sheet-table__station--total log-sheet-table__station--level-' + (row.row_level ?? 0)">
                          @if (hasHierarchyChildren(row)) {
                            <button
                              type="button"
                              class="log-sheet-table__hierarchy-toggle"
                              [attr.aria-label]="(isHierarchyExpanded(row) ? 'Collapse' : 'Expand') + ' ' + row.station_name"
                              (click)="toggleHierarchyRow(row); $event.stopPropagation()"
                            >
                              <span aria-hidden="true">{{ isHierarchyExpanded(row) ? '-' : '+' }}</span>
                            </button>
                          } @else {
                            <span class="log-sheet-table__hierarchy-toggle log-sheet-table__hierarchy-toggle--placeholder" aria-hidden="true"></span>
                          }
                          <strong>{{ row.station_name }}</strong>
                        </div>
                      </td>
                      @for (cell of row.blocks; track cell.block_no) {
                        <td
                          [class]="'log-sheet-table__hour-cell log-sheet-table__hour-cell--' + cell.period"
                          [class.peak-report-table__block-cell--peak]="isPeakBlock(cell)"
                        >
                          <span class="log-sheet-table__cell-text">{{ getCellText(cell) }}</span>
                        </td>
                      }
                    </tr>
                  } @else {
                    <tr
                      [class]="'log-sheet-table__hierarchy-row log-sheet-table__hierarchy-row--' + (row.row_kind ?? 'data')"
                      [class.log-sheet-table__hierarchy-row--power-small-generator]="row.is_small_power"
                    >
                      <td class="log-sheet-table__sticky-column">
                        <div
                          class="log-sheet-table__station"
                          [style.padding-left.rem]="row.station_name === 'TOTAL SYSTEM LOAD (MW)' ? 0 : 2.35"
                        >
                          @if (
                            row.row_kind === 'interruption-line' ||
                            row.row_kind === 'interruption-substation' ||
                            row.row_kind === 'power-thermal-generator' ||
                            row.row_kind === 'power-water-level-generator' ||
                            row.row_kind === 'power-spill-generator' ||
                            row.row_kind === 'power-kabeli-corridor-generator' ||
                            row.row_kind === 'power-frequency' ||
                            row.row_kind === 'power-small-generator' ||
                            row.is_small_power
                          ) {
                            <span>{{ row.station_name }}</span>
                          } @else {
                            <strong>{{ row.station_name }}</strong>
                          }
                        </div>
                      </td>
                      @for (cell of row.blocks; track cell.block_no) {
                        <td
                          [class]="'log-sheet-table__hour-cell log-sheet-table__hour-cell--' + cell.period"
                          [class.peak-report-table__block-cell--peak]="isPeakBlock(cell)"
                          [class.log-sheet-table__hour-cell--edited]="isEditedCell(cell)"
                          (dblclick)="beginCellEdit(row, cell)"
                        >
                          <div class="log-sheet-table__cell-content">
                            <span
                              class="log-sheet-table__cell-text"
                              [class.log-sheet-table__cell-text--empty]="cell.value === null || cell.value === undefined"
                            >
                              {{ getCellText(cell) }}
                            </span>
                            @if (cell.remarks) {
                              <button
                                type="button"
                                class="log-sheet-table__remark-indicator"
                                [attr.aria-label]="'Saved remarks for block ' + cell.label"
                              >
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                                  <circle cx="12" cy="12" r="9"></circle>
                                  <path d="M12 8h.01"></path>
                                  <path d="M11 11h1v5h1"></path>
                                </svg>
                                <span class="log-sheet-table__remark-tooltip" role="tooltip">{{ cell.remarks }}</span>
                              </button>
                            }
                          </div>
                        </td>
                      }
                    </tr>
                  }
                }
              } @else {
                <tr>
                  <td [attr.colspan]="getEmptyColspan()" class="log-sheet-page__empty-state"><span class="log-sheet-page__empty-text">No generation data found for the selected filters</span></td>
                </tr>
              }
            </tbody>
          </table>
        </div>

        <div class="log-sheet-page__legend" aria-label="Peak report legend">
          <span class="log-sheet-page__legend-item">
            <span class="log-sheet-page__legend-swatch log-sheet-page__legend-swatch--owner" aria-hidden="true"></span>
            <span>Owner</span>
          </span>
          <span class="log-sheet-page__legend-item">
            <span class="log-sheet-page__legend-swatch log-sheet-page__legend-swatch--source-type" aria-hidden="true"></span>
            <span>Source-Type</span>
          </span>
          <span class="log-sheet-page__legend-item">
            <span class="log-sheet-page__legend-swatch log-sheet-page__legend-swatch--fuel-type" aria-hidden="true"></span>
            <span>Fuel-Type</span>
          </span>
          <span class="log-sheet-page__legend-item">
            <span class="log-sheet-page__legend-swatch log-sheet-page__legend-swatch--substation" aria-hidden="true"></span>
            <span>Substation</span>
          </span>
           <span class="log-sheet-page__legend-item">
            <span class="log-sheet-page__legend-swatch log-sheet-page__legend-swatch--highlight" aria-hidden="true"></span>
            <span>Highlight</span>
          </span>
        </div>

      </div>
    </section>
  </main>

  @if (editDataModalOpen) {
    <div class="app-modal-backdrop">
      <dialog class="app-modal log-sheet-edit-modal" open>
        <div class="log-sheet-edit-modal__header">
          <div>
            <h3 class="app-title-serif">Edit Data</h3>
            <p>Update the selected cell value or copy the previous block value before saving.</p>
          </div>

          <button
            type="button"
            class="app-button app-button--icon log-sheet-edit-modal__close"
            aria-label="Close edit data modal"
            (click)="closeEditDataModal()"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round">
              <path d="M6 6 18 18"></path>
              <path d="M18 6 6 18"></path>
            </svg>
          </button>
        </div>

        <form [formGroup]="remarksForm" (ngSubmit)="saveEditData()">
          <div class="log-sheet-edit-modal__body">
            <label class="app-form-field">
              <span class="app-form-label">Data Value</span>
              <input
                class="app-form-input"
                type="number"
                step="1"
                inputmode="numeric"
                placeholder="Enter data value"
                formControlName="data_value"
                (input)="onEditDataValueInput($event)"
              />
            </label>

            <div class="log-sheet-edit-modal__radio-row  radio_inline" role="radiogroup" aria-label="Previous value source">
              <label class="log-sheet-edit-modal__radio-option">
                <input
                  type="radio"
                  name="previous-value-source"
                  [checked]="remarksForm.get('copy_previous_block_value')?.value"
                  [disabled]="arePreviousValueControlsDisabled()"
                  (change)="onPreviousValueModeChange('copy')"
                />
                <span>Copy Previous Block Value</span>
              </label>

              <label class="log-sheet-edit-modal__radio-option">
                <input
                  type="radio"
                  name="previous-value-source"
                  [checked]="remarksForm.get('carry_over_previous_day_values')?.value"
                  [disabled]="arePreviousValueControlsDisabled()"
                  (change)="onPreviousValueModeChange('carry')"
                />
                <span>Carry Over Previous Day Values</span>
              </label>
            </div>

            <label class="app-form-field">
              <span class="app-form-label">Remarks</span>
              <textarea
                class="app-form-input app-form-textarea log-sheet-edit-modal__textarea"
                rows="5"
                placeholder="Optional remarks"
                formControlName="remarks"
              ></textarea>
            </label>
          </div>

          <div class="log-sheet-edit-modal__footer">
            <button type="button" class="app-button app-button--secondary" (click)="closeEditDataModal()">Cancel</button>
            <button type="submit" class="app-button app-button--primary">Save</button>
          </div>
        </form>
      </dialog>
    </div>
  }
</app-report-shell>
